import { DestroyRef, Injectable, inject } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '../toast/toast.service';
import { BulkEditJobDetailsDialogComponent, type BulkEditJobDetailsDialogData } from './bulk-edit-job-details-dialog.component';
import { BulkEditService } from './bulk-edit.service';
import { BULK_UPDATE_QUEUE } from './bulk-edit.models';
import type { BulkJobRehydration } from './bulk-edit.models';
import type { BulkJobToastData } from './bulk-edit-inline.types';

@Injectable({ providedIn: 'root' })
export class BulkEditJobDetailsService {
  private static nextDialogId = 0;

  private readonly dialog = inject(Dialog);
  private readonly bulkEditService = inject(BulkEditService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  open(resourcePath: string, job: BulkJobToastData): void {
    this.bulkEditService
      .rehydrate(resourcePath, job.jobId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: rehydration => this.openDialog(job, rehydration),
        error: () => this.toast.error(this.translate.instant('common.error')),
      });
  }

  openById(resourcePath: string, jobId: string): void {
    this.open(resourcePath, {
      jobId,
      total: 0,
      queueName: BULK_UPDATE_QUEUE,
    });
  }

  private openDialog(job: BulkJobToastData, rehydration: BulkJobRehydration): void {
    const titleId = `zag-bulk-edit-job-details-dialog-title-${BulkEditJobDetailsService.nextDialogId++}`;
    const descriptionId = `${titleId}-description`;
    const data: BulkEditJobDetailsDialogData = { job, rehydration, titleId, descriptionId };
    this.dialog.open<void, BulkEditJobDetailsDialogData>(BulkEditJobDetailsDialogComponent, {
      data,
      panelClass: ['zag-dialog-panel', 'zag-dialog-panel--lg', 'zag-dialog-panel--full-size'],
      backdropClass: 'zag-dialog-backdrop',
      ariaLabelledBy: titleId,
      ariaDescribedBy: descriptionId,
    });
  }
}
